import { Injectable } from "@angular/core";
import { ThemeService } from "../organization";
import { Colors } from "./color.service";
import { ConstantService } from "./constant.service";
import { Observable } from "rxjs";
import { environment } from "../../../environments/environment";

@Injectable({
  providedIn: 'root',
})

export class ThemingService {
  public isLoaded: boolean;
  public companyLogo: string;
  public companyBackground: string;
  public colors: ThemeColors = new ThemeColors('#17C8E5', '#262C68');

  public backgroundImageCss: string;

  constructor(
    private constantService: ConstantService,
    private themeService: ThemeService

  ) { }

  async getTheme() {
    let cachedTheme: WebTheme = JSON.parse(window.localStorage.getItem('am_webTheme'));
    let theme: WebTheme = null;
    let request: Observable<any> = null;
    if (cachedTheme) {
      request = this.themeService.apiThemeByNameOrganizationMnemonicTypeLastCheckedGet(this.constantService.getSubdomain(), 'web', cachedTheme.lastChecked?.toString());
    }
    else {
      request = this.themeService.apiThemeByNameOrganizationMnemonicTypeGet(this.constantService.getSubdomain(), 'web');
    }
    await request.toPromise().then(
      async (data: any) => {
        if (data) {
          theme = {
            primaryColor: data.properties.PrimaryColor,
            secondaryColor: data.properties.SecondaryColor,
            companyLogo: data.properties.CompanyLogo,
            companyBackground: data.properties.CompanyBackground,
            favicon: data.properties.Favicon,
            lastChecked: new Date()
          }
        } else {
          this.resetTheme();
          theme = null;
          cachedTheme = null;

        }
      },
      (err) => {
        if (err?.status == 304) {
          theme = cachedTheme;
          theme.lastChecked = new Date();
        }
      }
    )
    if (!theme) {
      if (cachedTheme) {
        theme = cachedTheme;
      } else {
        theme = {
          primaryColor: '#17C8E5',
          secondaryColor: '#262C68'
        }
      }
    }
    await this.setTheme(theme);
  }

  public getFullImageUrl(imageUrl: string) {
    if (imageUrl.startsWith('v1/')) {
      var filestoreUrl = environment.filestore.indexOf('/api/') >= 0? environment.filestore + '/Filestore/GetImageByID/' : environment.filestore + '/api/Filestore/GetImageByID/';
      return filestoreUrl + imageUrl.substring(3);
    }
    var orgUrl = environment.organization.indexOf('/api/') >= 0 ? environment.organization + '/theme/image/' : environment.organization + '/api/theme/image/';
    return orgUrl + this.constantService.getSubdomain() + '/web/' + imageUrl;   
  }

  private async setTheme(theme: WebTheme) {
    await Promise.all([
      this.setImage(theme),
      this.setColors(theme),
      this.setFavIcon(theme)
    ]);
    if (theme.lastChecked) {
      window.localStorage.setItem('am_webTheme', JSON.stringify(theme));
    }
    this.isLoaded = true;
  }

  public resetTheme() {
    window.localStorage.removeItem('am_webTheme');
  }

  private async setImage(theme: WebTheme) {
    this.companyLogo = theme.companyLogo ? this.getFullImageUrl(theme.companyLogo) : ThemeEnum.companyLogoDefault;
    if (theme.companyBackground) {
      this.backgroundImageCss = "url('" + this.getFullImageUrl(theme.companyBackground) + "')";
    } else {
      this.backgroundImageCss = "linear-gradient(90deg, var(--theme-color-primary) 0%, var(--theme-color-secondary) 100%), linear-gradient(white), url('" + ThemeEnum.companyBackgroundDefault + "')";
    }   
  }

  private setFavIcon(theme: WebTheme) {
    let favicon: string = ThemeEnum.faviconDefault;
    if (theme.favicon) {
      favicon = this.getFullImageUrl(theme.favicon);
    }
    document.getElementById("favIcon").setAttribute("href", favicon);
  }

  private setColors(theme: WebTheme) {
    this.colors = new ThemeColors(theme.primaryColor, theme.secondaryColor);
    document.documentElement.style.setProperty(`--theme-color-primary`, this.colors.primaryColor);
    document.documentElement.style.setProperty(`--theme-color-secondary`, this.colors.secondaryColor);
    document.documentElement.style.setProperty(`--theme-color-primary-contrasting`, this.colors.primaryColorContrasting);
    document.documentElement.style.setProperty(`--theme-color-secondary-contrasting`, this.colors.secondaryColorContrasting);
    document.documentElement.style.setProperty(`--theme-color-primary-lighter-10`, this.colors.primaryColorLighter10);
    document.documentElement.style.setProperty(`--theme-color-primary-lighter-25`, this.colors.primaryColorLighter25);
    document.documentElement.style.setProperty(`--theme-color-primary-lighter-50`, this.colors.primaryColorLighter50);
    document.documentElement.style.setProperty(`--theme-color-primary-lighter-75`, this.colors.primaryColorLighter75);
    document.documentElement.style.setProperty(`--theme-color-primary-lighter-90`, this.colors.primaryColorLighter90);
    document.documentElement.style.setProperty(`--theme-color-primary-darker-10`, this.colors.primaryColorDarker10);
    document.documentElement.style.setProperty(`--theme-color-primary-darker-25`, this.colors.primaryColorDarker25);
    document.documentElement.style.setProperty(`--theme-color-primary-darker-50`, this.colors.primaryColorDarker50);
    document.documentElement.style.setProperty(`--theme-color-secondary-lighter-10`, this.colors.secondaryColorLighter10);
    document.documentElement.style.setProperty(`--theme-color-secondary-lighter-25`, this.colors.secondaryColorLighter25);
    document.documentElement.style.setProperty(`--theme-color-secondary-lighter-50`, this.colors.secondaryColorLighter50);
    document.documentElement.style.setProperty(`--theme-color-secondary-lighter-75`, this.colors.secondaryColorLighter75);
    document.documentElement.style.setProperty(`--theme-color-secondary-lighter-90`, this.colors.secondaryColorLighter90);
    document.documentElement.style.setProperty(`--theme-color-secondary-darker-10`, this.colors.secondaryColorDarker10);
    document.documentElement.style.setProperty(`--theme-color-secondary-darker-25`, this.colors.secondaryColorDarker25);
    document.documentElement.style.setProperty(`--theme-color-secondary-darker-50`, this.colors.secondaryColorDarker50);
  }

}

export class ThemeColors {
  private static readonly semanticColors = {
    warning: '#ffc107',
    info: '#17a2b8',
    success: '#28a745',
    danger: '#dc3545'
  };

  primaryColor: string;
  secondaryColor: string;
  readonly primaryColorLighter10: string;
  readonly primaryColorLighter25: string;
  readonly primaryColorLighter50: string;
  readonly primaryColorLighter75: string;
  readonly primaryColorLighter90: string;
  readonly primaryColorDarker10: string;
  readonly primaryColorDarker25: string;
  readonly primaryColorDarker50: string;
  readonly primaryColorContrasting: string;
  readonly secondaryColorLighter10: string;
  readonly secondaryColorLighter25: string;
  readonly secondaryColorLighter50: string;
  readonly secondaryColorLighter75: string;
  readonly secondaryColorLighter90: string;
  readonly secondaryColorDarker10: string;
  readonly secondaryColorDarker25: string;
  readonly secondaryColorDarker50: string;
  readonly secondaryColorContrasting: string;
  readonly warningColor: string;
  readonly infoColor: string;
  readonly successColor: string;
  readonly dangerColor: string;


  constructor(primaryColor: string, secondaryColor: string) {
    Colors.clearKnownColors();
    this.primaryColor = primaryColor;
    Colors.setKnownColor('primary', primaryColor);
    this.secondaryColor = secondaryColor;
    Colors.setKnownColor('secondary', secondaryColor);
    Object.entries(ThemeColors.semanticColors).forEach(([description, hex]) => {
      Colors.setKnownColor(description, hex);
    });
    this.primaryColorLighter10 = Colors.getColorByDescription('primary-lighter-10').toHexString();
    this.primaryColorLighter25 = Colors.getColorByDescription('primary-lighter-25').toHexString();
    this.primaryColorLighter50 = Colors.getColorByDescription('primary-lighter-50').toHexString();
    this.primaryColorLighter75 = Colors.getColorByDescription('primary-lighter-75').toHexString();
    this.primaryColorLighter90 = Colors.getColorByDescription('primary-lighter-90').toHexString();
    this.primaryColorDarker10 = Colors.getColorByDescription('primary-darker-10').toHexString();
    this.primaryColorDarker25 = Colors.getColorByDescription('primary-darker-25').toHexString();
    this.primaryColorDarker50 = Colors.getColorByDescription('primary-darker-50').toHexString();
    this.primaryColorContrasting = Colors.getColorByDescription('primary-contrasting').toHexString();
    this.secondaryColorLighter10 = Colors.getColorByDescription('secondary-lighter-10').toHexString();
    this.secondaryColorLighter25 = Colors.getColorByDescription('secondary-lighter-25').toHexString();
    this.secondaryColorLighter50 = Colors.getColorByDescription('secondary-lighter-50').toHexString();
    this.secondaryColorLighter75 = Colors.getColorByDescription('secondary-lighter-75').toHexString();
    this.secondaryColorLighter90 = Colors.getColorByDescription('secondary-lighter-90').toHexString();
    this.secondaryColorDarker10 = Colors.getColorByDescription('secondary-darker-10').toHexString();
    this.secondaryColorDarker25 = Colors.getColorByDescription('secondary-darker-25').toHexString();
    this.secondaryColorDarker50 = Colors.getColorByDescription('secondary-darker-50').toHexString();
    this.secondaryColorContrasting = Colors.getColorByDescription('secondary-contrasting').toHexString();
    this.warningColor = Colors.getColorByDescription('warning').toHexString();
    this.infoColor = Colors.getColorByDescription('info').toHexString();
    this.successColor = Colors.getColorByDescription('success').toHexString();
    this.dangerColor = Colors.getColorByDescription('danger').toHexString();
  }
}

export enum ThemeEnum {
  companyLogo = 'companyLogo',
  companyLogoDefault = '/assets/images/logo.png',
  companyBackground = 'homeBackground',
  companyBackgroundDefault = '/assets/images/home.png',
  primaryColor = 'primaryColor',
  secondaryColor = 'secondaryColor',
  favicon = 'favicon',
  faviconDefault = '/favicon.ico',
}

export interface WebTheme {
  primaryColor: string,
  secondaryColor: string,
  companyLogo?: string,
  companyBackground?: string,
  favicon?: string,
  lastChecked?: Date
}
